import { useState } from "react";
import { Alert, Box, Button, FormControlLabel, Stack, Switch, Tab, Tabs, TextField, MenuItem, Tooltip } from "@mui/material";
import List from "@mui/icons-material/FormatListBulleted";
import SyntheticKlvTemplateDialog from "./SyntheticKlvTemplateDialog";
import RateControlFields from "./RateControlFields";
import FramerateField from "./FramerateField";
import { defaultSyntheticKlvTemplateJson } from "../syntheticKlvTemplate.mjs";
export type FlywheelFormValues = {
  enabled: boolean;
  flywheelTextEnabled: boolean;
  flywheelText: string;
  processorId: string;
  engineId: string;
  videoCodec: "h264" | "h265";
  rateControl: "cbr" | "vbr";
  videoBitrateKbps: number;
  videoMaxBitrateKbps: number;
  videoKeyframeInterval: number;
  videoPid: number;
  framerateFps: string;
  preserveDetectedKlvType: boolean;
  resolutionWidth: number;
  resolutionHeight: number;
  preferHardwareAcceleration: boolean;
  syntheticKlvEnabled: boolean;
  syntheticKlvDefaultPid: number;
  klvType: "async" | "sync";
  syntheticKlvRate: number;
  syntheticKlvTemplateJson: string;
};

export const createDefaultFlywheelValues = (): FlywheelFormValues => ({
  enabled: false,
  flywheelTextEnabled: false,
  flywheelText: "",
  processorId: "",
  engineId: "stview-flywheel",
  videoCodec: "h264",
  rateControl: "cbr",
  videoBitrateKbps: 6000,
  videoMaxBitrateKbps: 6000,
  videoKeyframeInterval: 60,
  videoPid: 257,
  framerateFps: "30",
  preserveDetectedKlvType: true,
  resolutionWidth: 1280,
  resolutionHeight: 720,
  preferHardwareAcceleration: true,
  syntheticKlvEnabled: true,
  syntheticKlvDefaultPid: 258,
  klvType: "async",
  syntheticKlvRate: 10,
  syntheticKlvTemplateJson: defaultSyntheticKlvTemplateJson
});


type Props = {
  value: FlywheelFormValues; onChange: (value: FlywheelFormValues) => void;
  disabled?: boolean; readOnlyReason?: string; referencedTargets?: string[];
  readOnly?: boolean;
  authorizedFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  streamId?: string; running?: boolean; canApplyLive?: boolean;
};
export default function FlywheelParams({ value, onChange, authorizedFetch, disabled = false, readOnlyReason, referencedTargets = [], streamId, running = false, canApplyLive = false, readOnly = false }: Props) {
  const [tab, setTab] = useState("video"); const [templateOpen, setTemplateOpen] = useState(false);
  const off = disabled || readOnly || !value.enabled; const klvOff = off || !value.syntheticKlvEnabled;
  const change = <K extends keyof FlywheelFormValues>(key: K, next: FlywheelFormValues[K]) => { if (!readOnly) onChange({ ...value, [key]: next }); };
  const number = (label: string, key: keyof FlywheelFormValues, min: number, max: number, blocked = off) => (
    <Tooltip title={`Valid range: ${min}-${max}.`} arrow><TextField label={label} type="number" value={value[key]} disabled={blocked}
      fullWidth onChange={e => onChange({ ...value, [key]: Number(e.target.value) })} slotProps={{ htmlInput: { min, max, step: 1 } }} /></Tooltip>);
  const grid = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 };
  return <Stack spacing={1.5}>
    {readOnlyReason && <Alert severity="warning">{readOnlyReason}</Alert>}
    {value.enabled && referencedTargets.length > 0 && <Alert severity="warning">Reassign or remove these Flywheel outputs before disabling: {referencedTargets.join(", ")}</Alert>}
    <FormControlLabel control={<Switch checked={value.enabled} disabled={disabled || readOnly || (value.enabled && referencedTargets.length > 0)} onChange={e => change("enabled", e.target.checked)} />} label="Enable Flywheel" sx={{ ml: 0, width: "fit-content" }} />
    <Tabs value={tab} onChange={(_, next) => setTab(next)} variant="fullWidth" aria-label="Flywheel settings"><Tab value="video" label="Video" /><Tab value="klv" label="KLV" /></Tabs>
    {tab === "video" ? <Stack spacing={1.5} sx={{ opacity: off ? 0.58 : 1 }}>
      <Box sx={grid}>{number("Default PID", "videoPid", 32, 8186)}<TextField select label="Codec" value={value.videoCodec} disabled={off} onChange={e => change("videoCodec", e.target.value as "h264" | "h265")}><MenuItem value="h264">H.264</MenuItem><MenuItem value="h265">H.265</MenuItem></TextField></Box>
      <Box sx={{ ...grid, gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(4,minmax(0,1fr))" } }}>
        {number("Width", "resolutionWidth", 320, 8192)}{number("Height", "resolutionHeight", 240, 8192)}
        <FramerateField value={value.framerateFps} onChange={v => change("framerateFps", v)} disabled={off} max={60} />{number("Keyframe interval", "videoKeyframeInterval", 1, 600)}
      </Box>
      <RateControlFields rateControl={value.rateControl} bitrate={value.videoBitrateKbps} maxBitrate={value.videoMaxBitrateKbps} disabled={off}
        onRateControlChange={v => change("rateControl", v as "cbr" | "vbr")} onBitrateChange={v => change("videoBitrateKbps", Number(v))} onMaxBitrateChange={v => change("videoMaxBitrateKbps", Number(v))} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <FormControlLabel control={<Switch checked={value.flywheelTextEnabled} disabled={off} onChange={e => change("flywheelTextEnabled", e.target.checked)} />} label="Text" sx={{ ml: 0, mr: 0, flexShrink: 0 }} />
        <TextField fullWidth value={value.flywheelText} disabled={off || !value.flywheelTextEnabled} onChange={e => change("flywheelText", e.target.value)} slotProps={{ htmlInput: { "aria-label": "Flywheel text" } }} />
      </Box>
      <FormControlLabel control={<Switch checked={value.preferHardwareAcceleration} disabled={off} onChange={e => change("preferHardwareAcceleration", e.target.checked)} />} label="Prefer hardware acceleration" sx={{ ml: 0 }} />
    </Stack> : <Stack spacing={1.5} sx={{ opacity: off ? 0.58 : 1 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
        <FormControlLabel control={<Switch checked={value.syntheticKlvEnabled} disabled={off} onChange={e => change("syntheticKlvEnabled", e.target.checked)} />} label="Generate synthetic data" sx={{ ml: 0, mr: 0 }} />
        <Button startIcon={<List />} disabled={!readOnly && off && !canApplyLive} onClick={() => setTemplateOpen(true)}>{readOnly ? "View synthetic KLV fields" : "Edit synthetic KLV fields"}</Button>
      </Box>
      <FormControlLabel control={<Switch checked={value.preserveDetectedKlvType} disabled={off} onChange={e => change("preserveDetectedKlvType", e.target.checked)} />} label="Preserve detected source KLV type" sx={{ ml: 0 }} />
      <Box sx={{ ...grid, gridTemplateColumns: { xs: "1fr", sm: "repeat(3,minmax(0,1fr))" } }}>
        {number("Default PID", "syntheticKlvDefaultPid", 32, 8186, klvOff)}
        <TextField select label="KLV Type" disabled={off} value={value.klvType} onChange={e => change("klvType", e.target.value as "sync" | "async")}><MenuItem value="async">ASYNC</MenuItem><MenuItem value="sync">SYNC</MenuItem></TextField>
        {number("Fallback rate", "syntheticKlvRate", 1, 60, klvOff)}
      </Box>
      <SyntheticKlvTemplateDialog open={templateOpen} value={value.syntheticKlvTemplateJson} authorizedFetch={authorizedFetch} onClose={() => setTemplateOpen(false)} onSave={v => change("syntheticKlvTemplateJson", v)}
        readOnly={readOnly} applyEndpoint={!readOnly && streamId ? `/api/streams/${encodeURIComponent(streamId)}/flywheel/synthetic-klv/apply` : undefined} processorId={value.processorId} applyLabel={running ? "Save & apply" : "Save"} />
    </Stack>}
  </Stack>;
}
